import { type ErrorLike, NetworkStatus } from '@apollo/client';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { faToggleOff, faToggleOn } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import noop from 'lodash/noop';
import React from 'react';
import {
  ChatRoomWithMessageFragment,
  OrderDirection,
  UserBasicsFragment,
} from '../../graphql/types';
import useTracking from '../../hooks/useTracking';
import {
  EventCategories,
  EventNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { useChatContext } from '../Chat/Context';
import LoadingPanel from '../LoadingPanel';
import Button, {
  ButtonIconPosition,
  ButtonSize,
  ButtonVariant,
} from '../reusable/Button';
import GenericError from '../reusable/GenericError';
import LoadMore from '../reusable/LoadMore';
import PaginationWaypoint from '../reusable/PaginationWaypoint';
import { SearchField } from '../reusable/SearchField';
import Text, { TextColor, TextKind } from '../reusable/Text';
import { ChatListItem } from './ChatListItem';
import {
  StyledChatList,
  StyledChatListContainer,
  StyledEmptyState,
  StyledHeaderContainer,
} from './styled';

interface ChatListProps {
  selfId: string | undefined;
  error?: ErrorLike;
  networkStatus?: NetworkStatus;
  chatsAndMembers: {
    chat: ChatRoomWithMessageFragment;
    member: UserBasicsFragment;
  }[];
  hasNextPage?: boolean;
  setNameSearch: (value: string | null) => void;
  sortOrder?: OrderDirection;
  onSortClick: () => void;
  onChatClick?: () => void;
  loadMore: () => Promise<void>;
  Header?: React.ReactElement;
}

export const ChatList: React.FC<ChatListProps> = ({
  selfId,
  error,
  networkStatus,
  chatsAndMembers,
  hasNextPage = false,
  setNameSearch,
  sortOrder = OrderDirection.Desc,
  onSortClick,
  onChatClick = noop,
  loadMore,
  Header,
}) => {
  const { track } = useTracking();
  const { filterByUnread, setFilterByUnread } = useChatContext();

  const handleSearch = (value: string) => {
    track(EventNames.SearchInitiated, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.ChatList,
    });
    setNameSearch(value);
  };

  const handleSearchFocus = () => {
    track(EventNames.SearchActivated, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.ChatList,
    });
  };

  const handleSearchClear = () => {
    track(EventNames.SearchCleared, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.ChatList,
    });
    setNameSearch(null);
  };

  const loading = networkStatus === NetworkStatus.loading;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const handlePagination = async () => {
    if (!isFetchingMore) {
      await loadMore();
    }
  };

  const sortButtonLabel =
    sortOrder === OrderDirection.Desc ? 'Newest to oldest' : 'Oldest to newest';
  return (
    <StyledChatListContainer data-testid="ChatList-Container">
      <StyledHeaderContainer>
        {Header || <Text kind={TextKind.TitleM}>Chats</Text>}
        <SearchField
          width="100%"
          onClear={handleSearchClear}
          onFocus={handleSearchFocus}
          onSearch={handleSearch}
        />
        <div
          style={{
            alignSelf: 'start',
            borderRadius: '99ch',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Button
            size={ButtonSize.Small}
            variant={ButtonVariant.Transparent}
            icon={
              <FontAwesomeIcon
                icon={
                  sortOrder === OrderDirection.Desc ? faArrowDown : faArrowUp
                }
                fontSize={12}
              />
            }
            trackingData={{
              eventName: EventNames.Sort,
              event_category: EventCategories.Chat,
              event_subcategory: EventSubCategories.ChatList,
              event_label: sortButtonLabel,
            }}
            iconPosition={ButtonIconPosition.Right}
            onClick={onSortClick}
          >
            {sortButtonLabel}
          </Button>

          <Button
            size={ButtonSize.Small}
            variant={ButtonVariant.Transparent}
            icon={
              <FontAwesomeIcon
                icon={filterByUnread ? faToggleOn : faToggleOff}
                fontSize={16}
              />
            }
            testId="chat-list-filter-unread"
            trackingData={{
              eventName: EventNames.UnreadMessageFilter,
              event_category: EventCategories.Chat,
              event_subcategory: EventSubCategories.ChatList,
              event_label: filterByUnread ? 'disable' : 'enable',
            }}
            iconPosition={ButtonIconPosition.Right}
            onClick={() => setFilterByUnread(!filterByUnread)}
          >
            Unread only
          </Button>
        </div>
      </StyledHeaderContainer>
      <StyledChatList>
        {loading && <LoadingPanel height={150} noBorder />}
        {!loading && error && (
          <StyledEmptyState>
            <GenericError noMargin testId="ChatList-Error" error={error} />
          </StyledEmptyState>
        )}
        {!loading &&
          !error &&
          chatsAndMembers?.map(({ chat, member }) => (
            <ChatListItem
              key={chat.id}
              member={member}
              chat={chat}
              onClick={onChatClick}
              selfId={selfId}
            />
          ))}
        {!loading && hasNextPage && (
          <>
            <LoadMore
              loadMore={handlePagination}
              isFetchingMore={isFetchingMore}
            />
            <PaginationWaypoint callback={handlePagination} />
          </>
        )}
        {!error && !loading && chatsAndMembers?.length === 0 && (
          <StyledEmptyState>
            <Text kind={TextKind.BodyM} color={TextColor.Secondary}>
              No chats found.
            </Text>
          </StyledEmptyState>
        )}
      </StyledChatList>
    </StyledChatListContainer>
  );
};
